import { useState, useEffect, useMemo } from "react";
import PropTypes from "prop-types";
import {
  Box,
  TextField,
  Typography,
  Button,
  Chip,
  ListItemText,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import GROUPS_CONSTANTS, { GROUP_ID_TYPES } from "../GroupsConstant";
import { getGroupIdValidationError, GROUP_ID_MAX_LENGTH } from "../groupIdValidation.util";
import { useHachwimsFacilityOptions } from "../Groups.hook";
import { useSelectedCustomerAccess } from "./SelectedCustomerAccessContext";
import {
  dataConnectionsFormSx,
  dataConnectionsHachwimsTagsScrollSx,
  dataConnectionsFieldRowSx,
  dataConnectionsHachwimsFieldRowSx,
  dataConnectionsHachwimsFieldLabelSx,
  dataConnectionsFieldLabelSx,
  dataConnectionsActionsRowSx,
} from "./GroupAccessDrawer.style";
import {
  FACILITIES_LOADING_ID,
  FACILITIES_LOADING_OPTION_CLASS,
  FACILITIES_LOADER_SIZE,
  DataConnectionsMqttControlsRow,
  DataConnectionsHachwimsControlsRow,
  DataConnectionsMqttSwitch,
  DataConnectionsHachwimsSwitch,
  StyledFacilitiesLoader,
  StyledFacilitiesInputLoader,
  FacilitiesLoadingOption,
  FacilityOptionRow,
  FacilityOptionIconBox,
  StyledHachwimsAutocomplete,
  dataConnectionsMqttFieldSx,
  hachwimsFacilitiesListboxStyle,
  getHachwimsAutocompleteSlotProps,
} from "./DataConnectionsTabContent.style";

const DC = GROUPS_CONSTANTS.groupAccessDrawer.DATA_CONNECTIONS;

const snapshot = (mqtt, hach, mqttOn = true, hachOn = true) => ({
  mqttSpb: String(mqtt ?? "").trim(),
  hachwims: Array.isArray(hach) ? [...hach] : [],
  mqttEnabled: Boolean(mqttOn),
  hachwimsEnabled: Boolean(hachOn),
});

const facilityOrderKey = (list) =>
  (Array.isArray(list) ? list : []).map((f) => f?.id).filter(Boolean).join("|");

const handleWhiteSpaces = (event) => {
  if (event.key === " " || event.keyCode === 32) {
    event.preventDefault();
  }
};

const DataConnectionsTabContent = ({ onApply, applying = false, groups }) => {
  const theme = useTheme();
  const selectedGroupForAccess = useSelectedCustomerAccess();
  const selectedMqttSource = selectedGroupForAccess?.dataConnections?.find(
    (o) => o?.source === "mqtt-spb"
  )?.groupId;
  const [mqttSpb, setMqttSpb] = useState(selectedMqttSource ?? "");
  const [groupIdError, setGroupIdError] = useState(null);
  const [facilitiesError, setFacilitiesError] = useState(null);
  const [selectedHachwims, setSelectedHachwims] = useState([]);
  const [baseline, setBaseline] = useState(() => snapshot("", [], true, true));
  const availableDataConnections = {
    mqttSpb: selectedGroupForAccess?.dataConnections?.some(
      (o) => o?.source === "mqtt-spb"
    ),
    hachwims: selectedGroupForAccess?.dataConnections?.some(
      (o) => o?.source === "hachwims"
    ),
  };
  const [mqttEnabled, setMqttEnabled] = useState(() =>
    Boolean(availableDataConnections?.mqttSpb)
  );
  const [hachwimsEnabled, setHachwimsEnabled] = useState(() =>
    Boolean(availableDataConnections?.hachwims)
  );

  useEffect(() => {
    const list = selectedGroupForAccess?.dataConnections ?? [];
    const hasMqtt = list.some((o) => o?.source === "mqtt-spb");
    const hasHach = list.some((o) => o?.source === "hachwims");
    const { groupId: gid = "" } =
      list.find((r) => r.source === "mqtt-spb") ?? {};
    const hach = list.find((o) => o?.source === "hachwims")?.facilities ?? [];
    const mqtt = String(gid).slice(0, GROUP_ID_MAX_LENGTH);
    const hachFacilities = hach.flatMap((f) => {
      if (f == null) return [];
      return [{ id: f.id, name: f.name }];
    });
    setMqttSpb(mqtt);
    setGroupIdError(null);
    setSelectedHachwims(hachFacilities);
    setBaseline(snapshot(mqtt, hachFacilities, hasMqtt, hasHach));
    setMqttEnabled(hasMqtt);
    setHachwimsEnabled(hasHach);
  }, [selectedGroupForAccess]);

  useEffect(() => {
    if (mqttEnabled && !mqttSpb?.trim()) {
      setGroupIdError(
        GROUPS_CONSTANTS.createGroupErrorMessages.idRequired.replace(
          "{idType}",
          GROUP_ID_TYPES.groupId
        )
      );
    }
  }, [mqttEnabled, mqttSpb]);

  useEffect(() => {
    if (hachwimsEnabled && selectedHachwims?.length === 0) {
      setFacilitiesError(GROUPS_CONSTANTS.createGroupErrorMessages.facilitiesRequired);
    } else {
      setFacilitiesError(null);
    }
  }, [hachwimsEnabled, selectedHachwims]);

  const {
    hachwimsDropdownOptions,
    loadingHachwimsOptions,
    onFacilityInputChange,
    onFacilityListboxScroll,
    onFacilitiesDropdownOpen,
    onFacilitiesDropdownClose,
  } = useHachwimsFacilityOptions(selectedHachwims, selectedGroupForAccess?.groupId);

  const hachwimsOptionsSelectedFirst = useMemo(() => {
    const opts = hachwimsDropdownOptions ?? [];
    const selected = Array.isArray(selectedHachwims) ? selectedHachwims : [];
    const selectedFilenames = new Set(
      selected.map((s) => s?.id).filter(Boolean)
    );
    const top = [];
    const seen = new Set();
    for (const sel of selected) {
      const fn = sel?.id;
      if (!fn || seen.has(fn)) continue;
      const match = opts.find((o) => o.id === fn);
      if (match) {
        top.push(match);
        seen.add(fn);
      }
    }
    const rest = opts.filter((o) => !selectedFilenames.has(o.id));
    return [...top, ...rest];
  }, [hachwimsDropdownOptions, selectedHachwims]);

  const hachwimsFacilityOptions = useMemo(() => {
    if (!loadingHachwimsOptions) return hachwimsOptionsSelectedFirst;
    return [
      ...hachwimsOptionsSelectedFirst,
      { id: FACILITIES_LOADING_ID, name: "" },
    ];
  }, [hachwimsOptionsSelectedFirst, loadingHachwimsOptions]);

  const hachwimsAutocompleteSlotProps = useMemo(
    () => getHachwimsAutocompleteSlotProps(theme.zIndex.modal),
    [theme.zIndex.modal]
  );

  const hachwimsListboxProps = useMemo(
    () => ({
      onScroll: onFacilityListboxScroll,
      style: hachwimsFacilitiesListboxStyle,
    }),
    [onFacilityListboxScroll]
  );

  const trimmed = mqttSpb.trim();
  const togglesChanged =
    mqttEnabled !== baseline.mqttEnabled ||
    hachwimsEnabled !== baseline.hachwimsEnabled;
  const mqttSectionChanged =
    mqttEnabled && trimmed !== baseline.mqttSpb;
  const hachwimsSectionChanged =
    hachwimsEnabled &&
    facilityOrderKey(selectedHachwims) !== facilityOrderKey(baseline.hachwims);
  const hasChanges =
    mqttSectionChanged || hachwimsSectionChanged || togglesChanged;

  const runGroupIdValidation = (val) => {
    const errorMessage = getGroupIdValidationError(
      val,
      groups,
      GROUP_ID_TYPES.groupId,
      baseline.mqttSpb
    );
    setGroupIdError(errorMessage);
    return errorMessage;
  };

  const handleApply = async () => {
    if (!hasChanges || typeof onApply !== "function") return;
    if (mqttEnabled && trimmed && runGroupIdValidation(mqttSpb)) return;

    const dataConnections = [];
    if (mqttEnabled) {
      dataConnections.push({
        source: "mqtt-spb",
        groupId: trimmed,
      });
    }
    if (hachwimsEnabled) {
      dataConnections.push({
        source: "hachwims",
        facilities: (selectedHachwims)
          ?.map((facility) => facility?.id)
          .filter(Boolean),
      });
    }
    const nextBaseline = snapshot(
      trimmed,
      selectedHachwims,
      mqttEnabled,
      hachwimsEnabled
    );
    if (dataConnections.length === 0) {
      setBaseline(nextBaseline);
      return;
    }

    try {
      await onApply({ dataConnections });
      setBaseline(nextBaseline);
    } catch {
    }
  };
  const handleOnChangeMqtt = (e) => {
    const next = e.target.value;
    if (next.length >= GROUP_ID_MAX_LENGTH) {
      runGroupIdValidation(next)
      e.preventDefault();
      return;
    }
    setMqttSpb(next);
    if (!next.trim()) {
      setGroupIdError(null);
    } else if (mqttEnabled) {
      runGroupIdValidation(next);
    }
  }
  return (
    <Box sx={dataConnectionsFormSx}>
      <Box sx={dataConnectionsFieldRowSx}>
        <Typography sx={dataConnectionsFieldLabelSx}>
          {DC.MQTT_SPB_TITLE}
        </Typography>
        <DataConnectionsMqttControlsRow>
          <DataConnectionsMqttSwitch
            size="small"
            checked={mqttEnabled}
            disabled={mqttEnabled && selectedMqttSource}
            onChange={(_, checked) => {
              setMqttEnabled(checked);
              if (!checked) {
                setGroupIdError(null);
              }
            }}
            inputProps={{ "aria-label": DC.MQTT_SPB_TOGGLE_ARIA }}
          />
          <TextField
            label={DC.MQTT_SPB_FIELD_LABEL}
            value={mqttSpb}
            disabled={!mqttEnabled}
            onChange={handleOnChangeMqtt}
            placeholder={DC.MQTT_SPB_PLACEHOLDER}
            fullWidth
            variant="outlined"
            sx={dataConnectionsMqttFieldSx}
            error={Boolean(groupIdError)}
            helperText={groupIdError || ""}
            inputProps={{
              maxLength: GROUP_ID_MAX_LENGTH,
              "aria-label": DC.MQTT_SPB_ARIA_LABEL,
            }}
            onKeyDown={(event) => {
              handleWhiteSpaces(event);
            }}
          />
        </DataConnectionsMqttControlsRow>
      </Box>
      <Box sx={dataConnectionsHachwimsFieldRowSx}>
        <Typography sx={dataConnectionsHachwimsFieldLabelSx}>
          {DC.HACHWIMS_TITLE}
        </Typography>
        <DataConnectionsHachwimsControlsRow>
          <DataConnectionsHachwimsSwitch
            size="small"
            checked={hachwimsEnabled}
            onChange={(_, checked) => setHachwimsEnabled(checked)}
            inputProps={{ "aria-label": DC.HACHWIMS_TOGGLE_ARIA }}
          />
          <StyledHachwimsAutocomplete
            multiple
            fullWidth
            disabled={!hachwimsEnabled}
            loading={loadingHachwimsOptions}
            options={hachwimsFacilityOptions}
            value={Array.isArray(selectedHachwims) ? selectedHachwims : []}
            onChange={(_, value) =>
              setSelectedHachwims(
                (Array.isArray(value) ? value : []).filter(
                  (v) => v?.id !== FACILITIES_LOADING_ID
                )
              )
            }
            getOptionDisabled={(option) => option?.id === FACILITIES_LOADING_ID}
            onOpen={onFacilitiesDropdownOpen}
            onClose={onFacilitiesDropdownClose}
            onInputChange={onFacilityInputChange}
            filterOptions={(options) => options}
            getOptionLabel={(o) => o?.name ?? ""}
            isOptionEqualToValue={(a, b) => a?.id === b?.id}
            disableCloseOnSelect
            slotProps={hachwimsAutocompleteSlotProps}
            ListboxProps={hachwimsListboxProps}
            renderOption={(props, option, { selected }) => {
              const { key, ...rest } = props;
              if (option?.id === FACILITIES_LOADING_ID) {
                const { style: _style, className, ...loadingOptionProps } = rest;
                return (
                  <FacilitiesLoadingOption
                    key={key}
                    {...loadingOptionProps}
                    className={
                      className
                        ? `${className} ${FACILITIES_LOADING_OPTION_CLASS}`
                        : FACILITIES_LOADING_OPTION_CLASS
                    }
                  >
                    <StyledFacilitiesLoader
                      aria-label="Loading facilities"
                      size={FACILITIES_LOADER_SIZE}
                    />
                  </FacilitiesLoadingOption>
                );
              }
              const SelectionIcon = selected ? CheckBoxIcon : CheckBoxOutlineBlankIcon;
              return (
                <FacilityOptionRow key={key} component="li" {...rest}>
                  <FacilityOptionIconBox>
                    <SelectionIcon fontSize="small" />
                  </FacilityOptionIconBox>
                  <ListItemText
                    primary={option?.name ?? ""}
                    primaryTypographyProps={{ variant: "body2", noWrap: true }}
                  />
                </FacilityOptionRow>
              );
            }}
            renderTags={(tagValue, getTagProps) =>
              tagValue.length === 0 ? null : (
                <Box sx={dataConnectionsHachwimsTagsScrollSx}>
                  {tagValue?.map((option, index) => (
                    <Chip
                      {...getTagProps({ index })}
                      variant="outlined"
                      size="small"
                      label={option?.name ?? ""}
                    />
                  ))}
                </Box>
              )
            }
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder={DC.HACHWIMS_PLACEHOLDER}
                label={DC.HACHWIMS_FIELD_LABEL}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loadingHachwimsOptions ? (
                        <StyledFacilitiesInputLoader
                          aria-label="Loading facilities"
                          size={FACILITIES_LOADER_SIZE}
                        />
                      ) : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
                inputProps={{
                  ...params.inputProps,
                  "aria-label": DC.HACHWIMS_ARIA_LABEL,
                }}
                error={Boolean(facilitiesError)}
                helperText={facilitiesError || ""}
              />
            )}
          />
        </DataConnectionsHachwimsControlsRow>
      </Box>
      <Box sx={dataConnectionsActionsRowSx}>
        <Button
          type="button"
          variant="contained"
          color="primary"
          disabled={
            applying ||
            !hasChanges ||
            (mqttEnabled && Boolean(groupIdError)) ||
            (hachwimsEnabled && Boolean(facilitiesError))
          }
          onClick={handleApply}
        >
          {GROUPS_CONSTANTS.groupAccessDrawer.APPLY}
        </Button>
      </Box>
    </Box>
  );
};

DataConnectionsTabContent.propTypes = {
  onApply: PropTypes.func,
  applying: PropTypes.bool,
  groups: PropTypes.array,
};

export default DataConnectionsTabContent;
